import { useState } from 'react';

import { CalendarClock } from 'lucide-react';

import type { BoardCard } from '@/api/schema';
import { Alert } from '@/components/feedback/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { todayIso } from '@/lib/dates';

import styles from './HandOffModal.module.css';

export type HandOff = {
  /** The day the board runs on. Required by the server. */
  rsr_scheduled_on: string;
};

/**
 * The gate on the last Flyst stage.
 *
 * Reaching Sponsor screening arranged is the hand-off to the RSR board, and that board
 * reads its columns off the date, so arriving without one means arriving nowhere. The
 * server refuses it either way; this exists so somebody is asked rather than shown a
 * 409 explaining what they should have sent.
 *
 */
export function HandOffModal({
  card,
  busy,
  onClose,
  onConfirm,
}: {
  card: BoardCard;
  busy: boolean;
  onClose: () => void;
  onConfirm: (handOff: HandOff) => void;
}) {
  const [date, setDate] = useState(todayIso());
  const [problem, setProblem] = useState<string | null>(null);

  const confirm = () => {
    if (!date || date < todayIso()) {
      setProblem('Choose today or a future screening date.');
      return;
    }

    const handOff: HandOff = { rsr_scheduled_on: date };
    onConfirm(handOff);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Arrange the sponsor screening"
      description={`${card.full_name} moves to the RSR board once this is booked.`}
      icon={<CalendarClock size={19} />}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={confirm} loading={busy}>
            Arrange it
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {problem ? (
          <Alert tone="danger">
            <b>{problem}</b>
          </Alert>
        ) : null}

        <div className={styles.pair}>
          <Field label="Date" required hint="The RSR board runs on this.">
            {({ id, describedBy }) => (
              <Input
                id={id}
                describedBy={describedBy}
                type="date"
                min={todayIso()}
                value={date}
                onChange={(event) => {
                  setDate(event.target.value);
                }}
              />
            )}
          </Field>
        </div>
        <p>Calendar meetings are scheduled separately.</p>
      </div>
    </Modal>
  );
}
